import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// NOTE: Do NOT use withCredentials on ANY request.
// Cloudflare overrides CORS to Access-Control-Allow-Origin: * which is
// incompatible with credentialed requests (browser blocks the response).
// All auth uses the JWT token via localStorage + Authorization header instead.

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const storedToken = localStorage.getItem('access_token');
      if (storedToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      } else {
        setUser(false);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch {
      localStorage.removeItem('access_token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password }
      );
      const userData = response.data;

      // Store token in localStorage as fallback when cookies fail (Cloudflare CORS)
      if (userData.token) {
        localStorage.setItem('access_token', userData.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${userData.token}`;
      }

      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = 'Login failed. Please try again.';
      
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
      }
      
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch {
      // Logout endpoint failure is non-critical — clear local state regardless
    } finally {
      localStorage.removeItem('access_token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(false);
    }
  };

  const updateProfile = async ({ name, phone }) => {
    const payload = {};
    if (name  !== undefined) payload.name  = name;
    if (phone !== undefined) payload.phone = phone;
    const response = await axios.patch(`${API}/auth/me`, payload);
    const data = response.data;
    if (data.token) {
      localStorage.setItem('access_token', data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    }
    setUser((prev) => ({ ...(prev || {}), ...data }));
    return data;
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user && user !== false,
    login,
    logout,
    checkAuth,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
